/**
 * LWI-1563 — MUI theme built from the Logikor design tokens.
 *
 *   import { createAppTheme } from "./design-system/theme";
 *   const theme = createAppTheme({ mode: "light" });
 *
 * Apps can pass overrides for the palette, the chrome keys (header, footer,
 * sidebar) and component overrides; they are deep-merged over the defaults.
 * Only `@mui/material` is imported, so this folder has no app dependencies.
 */
import { createTheme } from "@mui/material/styles";

import {
  breakpoints,
  color,
  common,
  fontFamily,
  fontWeight,
  baseFontSize,
  radius,
  shadow,
  spacingUnit,
  textStyles,
} from "./tokens";

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  Object.getPrototypeOf(value) === Object.prototype;

// Deep-merges plain objects; arrays, functions and primitives replace.
const merge = (target, source) => {
  if (!isPlainObject(source)) return target;
  const output = { ...target };
  Object.keys(source).forEach((key) => {
    output[key] =
      isPlainObject(target[key]) && isPlainObject(source[key])
        ? merge(target[key], source[key])
        : source[key];
  });
  return output;
};

const paletteFor = (mode) => {
  const c = color[mode];
  return {
    mode,
    // light/dark are left to MUI so an app that overrides `main` gets
    // matching variants; MUI computes the values recorded in tokens.js.
    primary: { main: c.primary.main, contrastText: c.primary.contrastText },
    secondary: {
      main: c.secondary.main,
      contrastText: c.secondary.contrastText,
    },
    success: { ...c.success },
    error: { ...c.error },
    warning: { ...c.warning },
    info: { ...c.info },
    background: { default: c.surface.default, paper: c.surface.paper },
    text: { ...c.text },
    divider: c.divider,
    action: { hover: c.action.hover },
  };
};

// Non-MUI theme keys read by the app shell (sidebar, app bar, footer).
const chromeFor = (mode) => {
  const c = color[mode];
  return {
    header: {
      color: c.header.text,
      background: c.header.background,
      search: { color: c.header.search },
      indicator: { background: c.header.indicator },
    },
    footer: {
      color: c.footer.text,
      background: c.footer.background,
    },
    sidebar: {
      color: c.sidebar.text,
      background: c.sidebar.background,
      activeMarker: c.sidebar.activeMarker,
      activeColor: c.sidebar.activeText,
      header: {
        color: c.sidebar.text,
        background: c.sidebar.background,
        brand: { color: common.white },
      },
      footer: {
        color: c.sidebar.text,
        background: c.sidebar.footer,
      },
      badge: {
        color: common.white,
        background: c.sidebar.badge,
      },
    },
  };
};

const typography = {
  fontFamily: fontFamily.sans,
  fontSize: baseFontSize,
  fontWeightLight: fontWeight.light,
  fontWeightRegular: fontWeight.regular,
  fontWeightMedium: fontWeight.medium,
  fontWeightBold: fontWeight.semibold,
  h1: { ...textStyles.h1 },
  h2: { ...textStyles.h2 },
  h3: { ...textStyles.h3 },
  h4: { ...textStyles.h4 },
  h5: { ...textStyles.h5 },
  h6: { ...textStyles.h6 },
  subtitle2: { ...textStyles.subtitle2 },
  body1: { ...textStyles.body1 },
  body2: { ...textStyles.body2 },
  button: { ...textStyles.button },
};

// LWI-1567 — elevation scale built from the named shadows (no new values):
// 1 = xs (Paper, app bar), 2–5 = card, 6–24 = popover (menus, pickers,
// snackbars, drawers, dialogs, the DataGrid panel).
const elevationShadow = (level) => {
  if (level === 0) return shadow.none;
  if (level === 1) return shadow.xs;
  return level <= 5 ? shadow.card : shadow.popover;
};
const shadows = Array.from({ length: 25 }, (_, level) => elevationShadow(level));

const components = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { ...textStyles.button },
      contained: {
        textShadow: "none",
        boxShadow: "none",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiInputBase-root": {
          fontSize: textStyles.body1.fontSize,
          lineHeight: textStyles.body1.lineHeight,
          letterSpacing: textStyles.body1.letterSpacing,
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontSize: textStyles.body1.fontSize,
        lineHeight: textStyles.body1.lineHeight,
        letterSpacing: textStyles.body1.letterSpacing,
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "hover",
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: "h6",
      },
    },
    styleOverrides: {
      action: {
        marginTop: "-4px",
        marginRight: "-4px",
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        boxShadow: shadow.card,
        backgroundImage: "none",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radius.md,
      },
      filled: {
        textShadow: shadow.text,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
};

/**
 * @param {object}  [options]
 * @param {"light"|"dark"} [options.mode="light"]
 * @param {string}  [options.name]        stored on theme.name
 * @param {object}  [options.palette]     MUI palette overrides
 * @param {object}  [options.header]      chrome overrides
 * @param {object}  [options.footer]
 * @param {object}  [options.sidebar]
 * @param {object}  [options.components]  MUI component overrides
 */
export function createAppTheme({
  mode = "light",
  name,
  palette = {},
  header = {},
  footer = {},
  sidebar = {},
  components: componentOverrides = {},
} = {}) {
  const chrome = chromeFor(mode);

  return createTheme(
    {
      spacing: spacingUnit,
      breakpoints: { values: { ...breakpoints } },
      components: merge(components, componentOverrides),
      typography,
      shadows,
      palette: merge(paletteFor(mode), palette),
    },
    {
      name,
      header: merge(chrome.header, header),
      footer: merge(chrome.footer, footer),
      sidebar: merge(chrome.sidebar, sidebar),
    }
  );
}

export default createAppTheme;
